import React, { useEffect, useState } from "react";
import { ExternalLink, Quote, Star } from "lucide-react";
import api from "../lib/api";

/**
 * Prefers LIVE Google reviews (via /api/google-reviews). If no Google API key is
 * configured, falls back to the testimonials published on the business's own
 * site. Star ratings are only ever rendered when a real rating value exists -
 * we never invent one.
 */
export default function Reviews() {
  const [own, setOwn] = useState(null);

  useEffect(() => {
    api.get("/reviews").then((r) => setOwn(r.data)).catch(() => setOwn({ reviews: [] }));
  }, []);

  if (!own) return null;

  const google = { configured: false, reviews: [] };
  const useGoogle = false;
  const list = own.reviews || [];
  if (!list.length) return null;

  return (
    <section className="section-nq" data-testid="reviews-section">
      <div className="container-nq">
        <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="eyebrow">Client Experiences</p>
            <h2 className="mt-2 text-3xl font-bold md:text-4xl">
              {useGoogle ? "Google Reviews" : "Testimonials"}
            </h2>
            {!useGoogle && (
              <p className="mt-2 text-xs text-[color:var(--nq-stone)]" data-testid="reviews-source-note">
                Customer testimonials published on our site.
              </p>
            )}
          </div>

          {/* aggregate shown ONLY when Google actually returns one */}
          {useGoogle && google.rating != null && (
            <a
              href={google.maps_url || "#"}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 rounded-xl border px-4 py-3"
              style={{ borderColor: "rgba(198,162,74,0.3)", background: "rgba(198,162,74,0.06)" }}
              data-testid="reviews-aggregate"
            >
              <div className="flex">
                {Array.from({ length: Math.round(google.rating) }).map((_, k) => (
                  <Star key={k} className="h-4 w-4 fill-current gold-text" />
                ))}
              </div>
              <div className="text-sm">
                <strong className="gold-text">{google.rating}</strong>
                <span className="text-[color:var(--nq-stone)]">
                  {google.total != null ? ` from ${google.total} Google reviews` : " on Google"}
                </span>
              </div>
              <ExternalLink className="h-3.5 w-3.5 gold-text" />
            </a>
          )}
        </div>

        <div className="grid gap-5 md:grid-cols-3">
          {list.slice(0, 3).map((r) => (
            <figure key={r.id} className="card-nq flex flex-col p-6" data-testid="review-card">
              <Quote className="h-7 w-7 gold-text opacity-50" />
              {/* review text rendered verbatim - never edited */}
              <blockquote className="mt-3 flex-1 whitespace-pre-line text-sm leading-relaxed text-[color:var(--nq-stone)]">
                {r.body}
              </blockquote>

              <figcaption className="mt-4 border-t pt-3" style={{ borderColor: "rgba(255,255,255,0.08)" }}>
                {r.rating != null && (
                  <div className="flex" data-testid="review-stars">
                    {Array.from({ length: r.rating }).map((_, k) => (
                      <Star key={k} className="h-3.5 w-3.5 fill-current gold-text" />
                    ))}
                  </div>
                )}
                <div className="mt-1.5 flex items-center gap-2">
                  {r.avatar && <img src={r.avatar} alt="" className="h-7 w-7 rounded-full" />}
                  {r.author_url ? (
                    <a href={r.author_url} target="_blank" rel="noreferrer" className="text-sm font-semibold hover:text-[color:var(--nq-gold)]">
                      {r.author}
                    </a>
                  ) : (
                    <p className="text-sm font-semibold text-[color:var(--nq-sand)]">{r.author}</p>
                  )}
                </div>
                {r.when && <p className="mt-0.5 text-xs text-[color:var(--nq-stone)]">{r.when}</p>}
                {r.review_url && (
                  <a href={r.review_url} target="_blank" rel="noreferrer" className="mt-1 inline-block text-xs gold-text hover:underline">
                    View on Google Maps
                  </a>
                )}
              </figcaption>
            </figure>
          ))}
        </div>

        {useGoogle && (
          <p className="mt-5 text-xs text-[color:var(--nq-stone)]" data-testid="google-attribution">
            Reviews are user-generated and sourced live from Google Maps.
          </p>
        )}
      </div>
    </section>
  );
}
